import styled from 'styled-components';
import { Alert, Avatar, Card, Col, Form, Image, Input, Rate, Row, Skeleton, Space, Typography } from 'antd';
import { CalendarOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { Button, Modal } from 'antd-mobile';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import moment from 'moment';
import useFetchQuery from '../../../hooks/useFetchQuery';
import useTranslations from '../../../hooks/useTranslations';
import ReviewImg from '../../../assets/img/Review.png';
import PageMobile from '../../../components/page-mobile';
import FormSubmit from '../../../components/form/FormSubmit';

const { Text } = Typography;

const CardTherapistStyled = styled(Card)`
    background-color: #f3fbfa;
    width: 100%;
    .ant-card-body {
        padding: 12px;

        .ant-space {
            width: 100%;
        }

        .therapist-info-detail {
            display: flex;
            align-items: center;
            gap: 4px !important;
        }
        .therapist-info {
            gap: 2.5px !important;
        }

        .therapist-img {
            width: 42px;
            height: 42px;
            border-radius: 2px;
        }
    }
`;

function ModalSuccess({ visible, onClose }) {
    const { t } = useTranslations();
    const navigate = useNavigate();
    const textBakToHome = t('BackToHome', 'Back to Home');
    return (
        <Modal
            visible={visible}
            closeOnMaskClick
            bodyStyle={{ maxHeight: '80vh' }}
            content={
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center',
                        rowGap: 16,
                        textAlign: 'center',
                    }}
                >
                    <Image src={ReviewImg} preview={false} width={140} height={140} />
                    <Text strong>Thanks for Your Feedback!</Text>
                    <Text>
                        We truly appreciate you taking the time to share your thoughts. Your input helps us enhance our services and deliver a better
                        experience for everyone.
                    </Text>

                    <Button
                        onClick={() => {
                            navigate('/');
                        }}
                        color="primary"
                        style={{ width: '100%', marginTop: 16 }}
                    >
                        {textBakToHome}
                    </Button>
                </div>
            }
            onClose={onClose}
        />
    );
}

function Review() {
    const { t } = useTranslations();
    const { bookingCode } = useParams();
    const [formInstance] = Form.useForm();
    const navigate = useNavigate();

    const [visible, setVisible] = useState(false);
    const [isLoadingSubmit, setIsLoadingSubmit] = useState(false);
    const textSubmitReview = t('SubmitReview', 'Submit Review');
    const textLeaveWithoutReview = t('LeaveWithoutReview', 'Leave Without Review');

    const { data = {}, isLoading } = useFetchQuery({
        url: `/bookings/code/${bookingCode}`,
        method: 'get',
        enabled: () => Boolean(bookingCode),
        onFetchSuccess: ({ data }) => {
            return data?.booking_detail;
        },
    });

    const onClose = () => {
        setVisible(false);
    };

    return (
        <>
            <PageMobile isPageHeader>
                <Row
                    style={{
                        rowGap: 16,
                        width: '100%',
                        display: 'flex',
                        justifyContent: 'center',
                    }}
                >
                    {isLoading ? (
                        <Skeleton avatar active={isLoading} />
                    ) : (
                        <CardTherapistStyled>
                            <Row align="middle">
                                <Col flex="auto">
                                    <Space>
                                        <Image style={{ borderRadius: 2 }} src={data?.service_image} width={38} height={38} />

                                        <div>
                                            <Space direction="vertical" className="therapist-detail">
                                                <Text>{data?.service_name}</Text>
                                                <div style={{ display: 'flex', gap: 8 }}>
                                                    <CalendarOutlined />
                                                    <Text>{moment.unix(data?.ctime).format('YYYY-MM-DD')}</Text>
                                                </div>
                                            </Space>
                                        </div>
                                    </Space>
                                </Col>
                                <Col flex="none">
                                    <Space>
                                        <Avatar src={data?.masseuse_profile_picture} size={24} />
                                        <Text>{data?.masseuse_name}</Text>
                                    </Space>
                                </Col>
                            </Row>
                        </CardTherapistStyled>
                    )}
                    <Text strong>How Was Your Experience?</Text>
                    <Text style={{ textAlign: 'center' }}>
                        We’d love your feedback on your recent session, it helps us improve and better serve your needs.
                    </Text>
                    <FormSubmit
                        setLoadingSubmit={setIsLoadingSubmit}
                        showButtonSubmit={false}
                        form={formInstance}
                        formAction="/reviews"
                        beforeSubmit={(formData) => {
                            return {
                                ...formData,
                                booking_id: data?.id,
                            };
                        }}
                        onSuccess={() => {
                            setVisible(true);
                        }}
                    >
                        <Form.Item name="rating" style={{ display: 'flex', justifyContent: 'center' }}>
                            <Rate style={{ fontSize: 32 }} />
                        </Form.Item>
                        <div>
                            <Form.Item name="comment">
                                <Input.TextArea placeholder="Had a fantastic session with the therapist!" rows={6} />
                            </Form.Item>
                            <Alert
                                style={{
                                    background: '#DFF8F5',
                                    padding: '4px 8px',
                                    fontSize: 12,
                                    marginTop: 8,
                                }}
                                description="Your review will be shared anonymously to ensure your privacy and comfort."
                                type="info"
                                icon={
                                    <InfoCircleOutlined
                                        style={{
                                            color: 'var(--primary-color)',
                                            width: 16,
                                            height: 16,
                                        }}
                                    />
                                }
                                showIcon
                            />
                        </div>
                    </FormSubmit>
                    <Row style={{ rowGap: 16, width: '100%' }}>
                        <Button
                            loading={isLoadingSubmit}
                            style={{ width: '100%' }}
                            color="primary"
                            onClick={() => {
                                formInstance.submit();
                            }}
                        >
                            {textSubmitReview}
                        </Button>
                        <Button
                            loading={isLoadingSubmit}
                            onClick={() => {
                                navigate('/');
                            }}
                            style={{ width: '100%', color: 'var(--primary-color)' }}
                        >
                            {textLeaveWithoutReview}
                        </Button>
                    </Row>
                </Row>
            </PageMobile>
            <ModalSuccess visible={visible} onClose={onClose} />
        </>
    );
}

export default Review;
